import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { Directory } from '@capacitor/filesystem';
import { ConnectionStatus } from '@capacitor/network';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { HelperFunctions, IListing, IPhoto, ISubPhoto, Listing, NGRX_NO_ACTION } from '@curbnturf/entities';
import { IPhotoForm, photoFormBuilder } from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { ImageUploadOptions } from '@curbnturf/image-processing';
import { ImageRecropModalComponent } from '@curbnturf/image-processing/src/lib/image-recrop/image-recrop-modal.component';
import { ImageUploadComponent } from '@curbnturf/image-processing/src/lib/image-upload/image-upload.component';
import { ListingFacade } from '@curbnturf/listing';
import * as ListingActions from '@curbnturf/listing/src/lib/+state/listing.actions';
import * as ListingActionTypes from '@curbnturf/listing/src/lib/+state/listing.actions';
import * as NetworkSelectors from '@curbnturf/network';
import { OfflineListingPhotosDbSet } from '@curbnturf/network';
import { Logger } from '@curbnturf/network/src/lib/log/logger';
import { PhotoService } from '@curbnturf/photo';
import { SignUpStateFacade } from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.facade';
import * as SignUpSelectors from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.selectors';
import { StatusFacade } from '@curbnturf/status';
import { DeleteConfirmationModalComponent } from '@curbnturf/ui/src/lib/delete-confirmation-modal/delete-confirmation-modal.component';
import { IonicModule, ModalController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DragulaModule } from 'ng2-dragula';
import { Subscription } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-listing-photos',
  templateUrl: './listing-photos.component.html',
  styleUrls: ['./listing-photos.component.scss'],
  standalone: true,
  imports: [
    DeleteConfirmationModalComponent,
    DragulaModule,
    FormsModule,
    ImageUploadComponent,
    MiscSvgComponent,
    NgFor,
    NgIf,
    ReactiveFormsModule,
    IonicModule,
  ],
  providers: [AlertFacade, Logger],
})
export class ListingPhotosComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup<{
    id: FormControl<number | null>;
    syncId: FormControl<string | null>;
    photos: FormArray<FormGroup<IPhotoForm>>;
  }>;
  @Input() listing?: Listing;
  @Input() autoSave: boolean = true;

  @Output() beforeUpdating: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() imageUpdated: EventEmitter<void> = new EventEmitter<void>();

  uploadOptions: ImageUploadOptions = {
    action: ListingActions.uploadListingImage,
    completeAction: ListingActions.listingImagesUpdated,
    croppieOptions: {
      // viewport: { height: 280, width: 652 },
      // boundary: { height: 300, width: 698 },
    },
    output: {
      size: { height: 546, width: 1270 },
      circle: false,
    },
  };

  connectionStatus: ConnectionStatus;

  listingComplete = listingComplete;

  webPhotos: { key: string; uri: string; caption?: string }[] = [];

  recropPhotoSelected: ISubPhoto | undefined = undefined;

  get offlineMode() {
    if (Capacitor.isNativePlatform()) {
      if (!this.connectionStatus.connected) {
        return true;
      }
    }
    return false;
  }

  get photos() {
    if (this.form) {
      return this.form.get('photos') as FormArray<FormGroup<IPhotoForm>>;
    }

    return;
  }

  get listingId(): number | undefined {
    if (this.listing && this.listing.id) {
      return this.listing.id;
    }

    if (this.form) {
      const id = this.form.get('id') as FormControl<number | null>;
      if (id && id.value) {
        return id.value;
      }
    }

    return;
  }

  get syncId(): string | undefined {
    if (this.listing && this.listing.syncId) {
      return this.listing.syncId;
    }

    if (this.form) {
      const syncId = this.form.get('syncId') as FormControl<string | null>;
      if (syncId && syncId.value) {
        return syncId.value;
      }
    }
    return;
  }

  subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private updates$: Actions,
    private cd: ChangeDetectorRef,
    private offlineListingPhotosDbSet: OfflineListingPhotosDbSet,
    private photoService: PhotoService,
    private signUpFacade: SignUpStateFacade,
    private listingFacade: ListingFacade,
    private statusFacade: StatusFacade,
    private store: Store,
    private modalController: ModalController,
    private alertFacade: AlertFacade,
    private logger: Logger,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.statusFacade.networkStatus$.subscribe((status) => {
        this.connectionStatus = status;
      }),
      this.updates$
        .pipe(
          ofType(ListingActionTypes.listingImagesUpdated),
          withLatestFrom(this.store.select(NetworkSelectors.getStatus)),
          withLatestFrom(this.store.select(SignUpSelectors.getListing)),
        )
        .subscribe(async ([[action, status], listing]) => {
          const photos = [...(action?.photos || [])];

          if (!status.connected) {
            for (let photo of action.photos) {
              const uri = await this.loadPhotoFromFile(photo.key);
              this.webPhotos.push({ ...photo, key: photo.key, uri, caption: photo.caption });
              // Push the photo to the sign-up listing record.
              photos.push(photo);
            }
          }

          if (action?.photos) {
            this.signUpFacade.updateListingPhotos(photos, action.id);
            this.photos?.clear();
            photos?.forEach((photo) => this.photos?.push(photoFormBuilder(this.fb, photo)));
          }

          this.cd.detectChanges();
        }),
    );
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.updateListingId();

    if (changes.form?.currentValue) {
      const idControl = this.form.get('id') as FormControl<number | null>;
      if (idControl) {
        this.subscriptions.push(idControl.valueChanges.subscribe(() => this.updateListingId()));
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  getPhotoUrl(photo: FormGroup<IPhotoForm>) {
    return HelperFunctions.getPhotoUrlFlatSmall(photo.value as ISubPhoto);
  }

  removePhoto(index: number): void {
    if (this.listingId) {
      this.listingFacade.removePhoto(this.listingId, index);
      this.form.markAsDirty();
    }
  }

  removePhotoFunction(index: number): () => void {
    return () => this.removePhoto(index);
  }

  movePhotoUp(index: number): void {
    if (index === 0 || !this.photos) {
      return;
    }
    const photo = this.photos.at(index);
    this.photos.removeAt(index);
    this.photos.insert(index - 1, photo);
    this.save(false);
  }

  movePhotoDown(index: number): void {
    if (this.photos) {
      const photo = this.photos.at(index);
      this.photos.removeAt(index);
      this.photos.insert(index + 1, photo);
      this.save(false);
    }
  }

  photoOrder(index: number, item: FormGroup<IPhotoForm>) {
    return item?.value?.key;
  }

  updatePhotoOrder(photos: IPhoto[]) {
    photos.forEach((photo, index) => this.photos?.at(index).patchValue(photo));
    this.photos?.markAsDirty();
  }

  save(imageUpload = true) {
    this.beforeUpdating.emit(imageUpload);
    if (this.listingId) {
      this.signUpFacade.updateListingPhotoDetails(this.listingId, this.form.value.photos as IPhoto[]);
    } else {
      this.alertFacade.display({
        title: 'No Selected Site',
        body: 'There is no site selected to modify.',
        level: 'error',
      });
    }
    this.form.markAsPristine();
  }

  private getListingToSave(): IListing {
    if (!this.listing) {
      this.listing = new Listing();
    }

    return {
      ...this.listing,
      // This has to be simple as any undefined fields here would overwrite the good data in the site record.
      ...(this.form.value as IListing),
    };
  }

  private updateListingId() {
    this.uploadOptions.data = {
      listingId: this.listingId,
      syncId: this.syncId,
    };
  }

  async loadPhotoFromFile(photo: string): Promise<string> {
    // Working on a physical device.
    if (Capacitor.isNativePlatform()) {
      // Display the photo by reading into base64 format
      return (
        (await this.photoService.loadPhotoForWeb({ path: photo, format: 'jpeg', saved: false }, Directory.Data))
          .webPath || ''
      );
    } else {
      // We don't save locally on the web.
      return '';
    }
  }

  getOfflinePhoto(requestedPhoto: IPhoto): string {
    const photo = this.webPhotos.find((el) => {
      return el.key === requestedPhoto.key;
    });
    if (photo) {
      return photo.uri;
    } else {
      return '';
    }
  }

  removeOfflinePhotoFunction(filename: string) {
    return async () => {
      await this.offlineListingPhotosDbSet.removeByKey(filename as string);
      await this.photoService.removeOfflinePhoto(filename as string);
    };
  }

  imagesUpdated(): () => void {
    return () => {
      this.imageUpdated.emit();
      this.form.markAsDirty();
      if (this.autoSave) {
        this.save();
      }
    };
  }

  getCaptionControl(photo: FormGroup<IPhotoForm>): FormControl {
    return photo.get('caption') as FormControl;
  }

  async saveRecrop(values: FormData) {
    if (this.recropPhotoSelected) {
      const photo: ISubPhoto = {
        key: this.recropPhotoSelected.key,
        caption: values.get('caption') as string,
        cropLeft: parseInt(values.get('cropLeft') as string, 10),
        cropTop: parseInt(values.get('cropTop') as string, 10),
        cropRight: parseInt(values.get('cropRight') as string, 10),
        cropBottom: parseInt(values.get('cropBottom') as string, 10),
        zoomRatio: parseInt(values.get('zoom') as string, 10),
        rotation: parseInt(values.get('rotation') as string, 10),
      };
      this.beforeUpdating.emit(true);
      this.listingFacade.recropImage(this.getListingToSave(), photo);
      this.form.markAsPristine();
      await this.modalController.dismiss();
    } else {
      this.alertFacade.display({
        title: 'Selected Photo Not Found',
        body:
          "Something went wrong and we weren't about to find the photo you tried to crop. If the problem persists, " +
          'please contact support.',
        level: 'error',
      });
      this.logger.error(
        'Recrop Photo Not Found',
        'It looks like the photo that was requested for recropping no longer existed when returning to the listing-photos component.',
      );
    }
  }

  async recropPhoto(photo: FormGroup<IPhotoForm>) {
    this.recropPhotoSelected = photo.value as ISubPhoto;
    const modal = await this.modalController.create({
      component: ImageRecropModalComponent,
      componentProps: {
        photo: photo.value,
        options: {
          ...this.uploadOptions,
          action: NGRX_NO_ACTION,
        },
        saveFunction: (values: FormData) => this.saveRecrop(values),
      },
    });
    await modal.present();
  }
}
